import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useCurrentDataContext } from "../../../contexts/CurrentDataContext";
import { useErrorContext } from "../../../contexts/ErrorContext";
import { useRoutesContext } from "../../../contexts/RoutesContext";
import { useUserContext } from "../../../contexts/UserContext";
import { returnBackForm } from "../../../scripts/dataHandlers";
import classes from "./DoctorReturnConfirm.module.css";

interface Props {
  setConfirmNeeded: (val: boolean) => void;
  setIsHandling: (val: boolean) => void;
}

const DoctorReturnConfirm = ({ setConfirmNeeded, setIsHandling }: Props) => {
  const [input, setInput] = useState<string>("");

  const { user } = useUserContext();

  const { currentData, setCurrentData } = useCurrentDataContext();

  const { routes } = useRoutesContext();

  const { handleError } = useErrorContext();

  // Set translation
  const { t } = useTranslation();

  const navigate = useNavigate();

  const handleReturnToApproval = () => {
    // Clear textarea
    setInput("");
    // Set confirmNeeded to false, which removes this modal
    setConfirmNeeded(false);
  };

  const handleReturn = () => {
    setIsHandling(true);
    toast(`${t("message.handlingForm")}`, {
      toastId: "handlingForm",
      position: "top-center",
      autoClose: false,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
    });

    const doReturn = async () => {
      // Request returnBackForm to change form status back to "approval" in backend
      const doctorNameString = `${user?.firstName || ""} ${
        user?.lastName || ""
      }`;
      const res =
        user &&
        currentData?.formId &&
        currentData?.employeeId &&
        (await returnBackForm(
          user.id,
          currentData.formId,
          currentData.employeeId,
          input,
          doctorNameString
        ));

      setIsHandling(false);
      toast.dismiss("handlingForm");
      if (res?.status === 200) {
        // Success! -message about returning, after that navigate
        toast(`${t("message.formReturnedSuccessfully")}`, {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          type: "success",
        });
        setCurrentData(undefined);
        navigate(`/${routes.doctorMain}`);
      } else {
        handleError(res, "ReturnBackFormFailed");
      }
    };

    doReturn();
  };

  return (
    <div className={classes.confirmBackground}>
      <div className={classes.confirmModal}>
        <div className={classes.confirmTitle}>
          {t("doctorReturnConfirm.title")}
        </div>
        <div className={classes.confirmText}>
          {t("doctorReturnConfirm.infoText")}
        </div>

        <div className={classes.textAreaContainer}>
          <textarea
            className={classes.textArea}
            placeholder={t("doctorReturnConfirm.placeholder")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            rows={5}
            spellCheck={false}
          />
        </div>

        <div className={classes.buttonsContainer}>
          <button
            className={classes.returnToFormButton}
            onClick={() => handleReturnToApproval()}
          >
            {t("doctorReturnConfirm.returnToApproval")}
          </button>
          <button
            className={classes.returnButton}
            onClick={() => handleReturn()}
            disabled={input?.length === 0}
            style={{ opacity: `${input?.length > 0 ? "1" : "0.3"}` }}
          >
            {t("doctorReturnConfirm.return")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorReturnConfirm;
